/**
 * The "you are here" marker — legacy's `setCurrentRow`/`flashElement`.
 *
 * One row is current at a time — a `<tr>` in the chart of accounts and the
 * register, a transaction card in the reports, which is why nothing here
 * assumes a table. It carries a lasting amber tint and `aria-current`, and it
 * can be flashed brightly when the reader's attention needs bringing to it:
 * arriving back from an account's register, or landing on the transaction a
 * Transfer link pointed at. The flash decays *into* the lasting tint rather
 * than to nothing, which is the whole point — a cue that is gone by the time
 * you look up did not happen, as far as the reader is concerned.
 *
 * Two things are deliberately not done here. The marker is never removed on a
 * timer — only when another row takes it — because it answers "where was I",
 * a question that stays asked. And nothing is persisted: which row is current
 * is a property of this page in this tab, not of the person. What survives a
 * navigation is the preference the page consults on load.
 *
 * Lives in ui-web because the chart of accounts, the register and the reports
 * all want the same marker, and a second implementation of it would drift.
 */

/** How long the bright part lasts. Legacy's 1.5s, which is long enough to catch and short enough not to nag. */
const FLASH_MS = 1500;

export interface MarkCurrentRowOptions {
  /** Play the bright sweep. Off for a re-render, which is only re-applying what was already there. */
  flash?: boolean;
  /**
   * Whether to move the page to the row.
   *
   * `'if-needed'` leaves a row the reader can already see where it is — for
   * a *return*, where the position has just been restored and moving it
   * again would undo that. `'always'` centres it, for an explicit
   * destination: someone followed a link to see this one thing, and putting
   * it against the bottom edge of the window answers the letter of that and
   * not the point.
   */
  scroll?: 'if-needed' | 'always';
  /**
   * The attribute carrying the row's id. Each page keys its rows by whatever
   * they are *about* — accounts by account, the register and the reports by
   * transaction — so the marker is told where to look rather than imposing a
   * name on three different lists.
   */
  attr?: string;
}

/**
 * Move the marker to `id` within `container`, returning the element or `null`
 * if it is not rendered (collapsed, filtered out, deleted — all normal).
 */
export function markCurrentRow(
  container: HTMLElement, id: string | null, opts: MarkCurrentRowOptions = {},
): HTMLElement | null {
  const attr = opts.attr ?? 'data-row';
  const row = id ? container.querySelector<HTMLElement>(`[${attr}="${CSS.escape(id)}"]`) : null;

  for (const other of container.querySelectorAll<HTMLElement>('.wl-current')) {
    if (other === row) continue;
    other.classList.remove('wl-current');
    other.removeAttribute('aria-current');
  }
  if (!row) return null;

  row.classList.add('wl-current');
  // `true` rather than `location`: the row is the current item of a set, and
  // "true" is the value every screen reader handles. Without this the marker
  // would be colour and nothing else (invariant 11).
  row.setAttribute('aria-current', 'true');

  if (opts.scroll === 'always' || (opts.scroll === 'if-needed' && !isFullyVisible(row))) {
    // Instant, never smooth: a glide after a navigation reads as the page
    // drifting on its own rather than as an answer to what you just did.
    row.scrollIntoView({ block: 'center', behavior: 'instant' });
  }

  if (opts.flash) {
    // Restart rather than add: flashing a row that is already flashing (two
    // arrivals in quick succession) would otherwise do nothing visible.
    row.classList.remove('wl-flash');
    row.getBoundingClientRect();          // force a reflow so the animation replays
    row.classList.add('wl-flash');
    // Only the bright part is timed out. `wl-current` stays.
    setTimeout(() => row.classList.remove('wl-flash'), FLASH_MS);
  }
  return row;
}

/**
 * Whether the row is already wholly on screen.
 *
 * Centring a row the reader can already see is a pointless jump, and on a
 * return it also fights the position just restored — two things moving the
 * page at once, neither of which the reader asked for.
 */
/**
 * Whether the row can actually be seen: inside the window AND inside every
 * scrolling box around it.
 *
 * It asked the window alone. The wallet log scrolls in a pane of its own, so
 * a row below the pane's edge — clipped, invisible — still sat inside the
 * window and counted as visible, and a newly saved entry was marked out of
 * sight and never scrolled to. Reported as "a new entry shall be selected and
 * scrolled into view".
 */
function isFullyVisible(el: HTMLElement): boolean {
  const rect = el.getBoundingClientRect();
  let top = 0;
  let bottom = window.innerHeight || document.documentElement.clientHeight;
  for (let box = el.parentElement; box; box = box.parentElement) {
    const overflow = getComputedStyle(box).overflowY;
    if (overflow !== 'auto' && overflow !== 'scroll' && overflow !== 'hidden' && overflow !== 'clip') continue;
    const r = box.getBoundingClientRect();
    top = Math.max(top, r.top);
    bottom = Math.min(bottom, r.bottom);
  }
  return rect.top >= top && rect.bottom <= bottom;
}
